import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { BookOpen, Target, Award, BarChart2, CheckCircle2, XCircle, Trophy, Play, ChevronDown, ChevronUp, Eye, Layers, Zap, Star, Swords, Crown, KeyRound, Building2, ShieldCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TestReviewDialog } from "@/components/TestReviewDialog";
import { TournamentSection } from "@/components/TournamentSection";
import WelcomeToVenue from "@/components/WelcomeToVenue";
import UserProfilePopup from "@/components/UserProfilePopup";
import ContinueStudyingCard from "@/components/ContinueStudyingCard";
import CardReviewSummary from "@/components/CardReviewSummary";
import AccountSection from "@/components/AccountSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = {
  system_admin: "System Admin",
  org_admin: "Organisation Admin",
  admin: "Admin",
  venue_manager: "Venue Manager",
  staff: "Staff",
};

export default function UserDashboard() {
  const navigate = useNavigate();
  const [myResults, setMyResults] = useState(null);
  const [myTests, setMyTests] = useState([]);
  const [xpSummary, setXpSummary] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [masteryStats, setMasteryStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedTest, setExpandedTest] = useState(null);
  const [testLeaderboards, setTestLeaderboards] = useState({});
  const [reviewResultId, setReviewResultId] = useState(null);
  const [progressOpen, setProgressOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [resultsRes, testsRes, xpRes, userRes, masteryRes] = await Promise.all([
          axios.get(`${API}/user/my-results`),
          axios.get(`${API}/user/my-tests`),
          axios.get(`${API}/user/xp-summary`).catch(() => ({ data: null })),
          axios.get(`${API}/auth/me`).catch(() => ({ data: null })),
          axios.get(`${API}/user/mastery-stats`).catch(() => ({ data: null })),
        ]);
        setMyResults(resultsRes.data);
        setMyTests(testsRes.data);
        setXpSummary(xpRes.data);
        setCurrentUser(userRes.data);
        setMasteryStats(masteryRes.data);
      } catch (err) {
        console.error("Failed to fetch data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const toggleLeaderboard = async (testId) => {
    if (expandedTest === testId) {
      setExpandedTest(null);
      return;
    }
    setExpandedTest(testId);
    if (!testLeaderboards[testId]) {
      try {
        const res = await axios.get(`${API}/saved-tests/${testId}/leaderboard`);
        setTestLeaderboards(prev => ({ ...prev, [testId]: res.data }));
      } catch {
        setTestLeaderboards(prev => ({ ...prev, [testId]: { leaderboard: [] } }));
      }
    }
  };

  const getScoreColor = (pct) => {
    if (pct >= 80) return "text-green-500";
    if (pct >= 60) return "text-[#C36A2F]";
    return "text-red-500";
  };

  const getBarColor = (pct) => {
    if (pct >= 80) return "bg-green-500";
    if (pct >= 60) return "bg-[#C36A2F]";
    return "bg-red-500";
  };

  if (loading) {
    return (
      <div data-testid="user-dashboard" className="space-y-6">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">My Dashboard</h1>
        <p className="text-[hsl(var(--muted-foreground))]">Loading...</p>
      </div>
    );
  }

  const results = myResults?.results || [];
  const typeStats = myResults?.type_stats || {};
  const studyDecks = myResults?.study_decks || [];

  const avgScore = results.length > 0
    ? results.reduce((s, r) => s + (r.percentage || 0), 0) / results.length
    : 0;
  const bestScore = results.length > 0 ? Math.max(...results.map(r => r.percentage || 0)) : 0;

  return (
    <div data-testid="user-dashboard" className="space-y-6">
      <WelcomeToVenue />
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight" data-testid="user-dashboard-title">
            {currentUser?.name ? `${currentUser.name.split(' ')[0]}'s Dashboard` : "Dashboard"}
          </h1>
          {/* Phase 6 — role / venue / level clarity. Purely informational
              display pills; none of this is used for authorization (the
              backend already enforces that from the session, independent
              of anything rendered here). */}
          <div className="flex items-center gap-2 mt-2 flex-wrap" data-testid="identity-pills">
            {currentUser?.role && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]" data-testid="pill-role">
                <ShieldCheck size={12} /> {ROLE_LABELS[currentUser.role] || currentUser.role}
              </span>
            )}
            {currentUser?.venue_name && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]" data-testid="pill-venue">
                <Building2 size={12} /> {currentUser.venue_name}
              </span>
            )}
            {xpSummary?.level && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-[#C36A2F]/10 text-[#C36A2F] border border-[#C36A2F]/20" data-testid="pill-level">
                <Zap size={12} /> Level {xpSummary.level.level} &middot; {xpSummary.level.title}
              </span>
            )}
          </div>
          <p className="text-[hsl(var(--muted-foreground))] mt-2">Your tests, performance, and study materials</p>
        </div>
        {!currentUser?.org_id && (
          <Button
            onClick={() => setProfileOpen(true)}
            variant="outline"
            className="border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10"
            data-testid="open-profile-btn"
          >
            <KeyRound size={16} className="mr-2" />
            Join with code
          </Button>
        )}
        {currentUser?.org_id && (
          <Button
            onClick={() => setProfileOpen(true)}
            variant="ghost"
            size="sm"
            className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
            data-testid="open-profile-btn"
          >
            <KeyRound size={14} className="mr-1.5" />
            Switch venue
          </Button>
        )}
      </div>

      {/* ───── Continue Studying — the ONE prominent next action ───── */}
      <ContinueStudyingCard variant="hero" />

      {/* ───── Progress (XP/Level, Learning Mastery, Accuracy) ───── */}
      <div className="space-y-3" data-testid="progress-section">
        <p className="text-xs font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] px-1">Progress</p>

      {/* ───── Barback (XP & Progress) collapsible ───── */}
      {xpSummary && (
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))] overflow-hidden" data-testid="xp-card">
          <button
            type="button"
            onClick={() => setProgressOpen((o) => !o)}
            aria-expanded={progressOpen}
            className="w-full text-left transition-colors hover:bg-[hsl(var(--secondary))]/40"
            data-testid="barback-toggle"
          >
            <div className="flex items-center gap-4 p-4">
              <div className="w-14 h-14 rounded-full bg-[hsl(var(--primary))]/20 flex items-center justify-center shrink-0">
                <span className="text-[hsl(var(--primary))] text-xl font-bold">{xpSummary.level?.level}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-lg font-bold truncate">{xpSummary.level?.title}</p>
                  <ChevronDown
                    size={18}
                    className={`text-[hsl(var(--muted-foreground))] transition-transform shrink-0 ${progressOpen ? "rotate-180" : ""}`}
                  />
                </div>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <Zap size={14} className="text-[#C36A2F]" />
                  <span className="text-sm font-semibold text-[#C36A2F]">{xpSummary.total_xp} XP</span>
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">· {xpSummary.challenge_count} practice sessions</span>
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">· {results.length} tests · {bestScore.toFixed(0)}% best</span>
                </div>
                {(xpSummary.tournaments_entered > 0 || xpSummary.tournaments_won > 0) && (
                  <div className="flex items-center gap-3 mt-1 text-xs text-[hsl(var(--muted-foreground))]">
                    <span className="flex items-center gap-1"><Swords size={12} /> {xpSummary.tournaments_entered} entered</span>
                    <span className="flex items-center gap-1"><Crown size={12} className="text-[hsl(var(--primary))]" /> {xpSummary.tournaments_won} won</span>
                    <span>{xpSummary.tournament_points} pts earned</span>
                  </div>
                )}
                {xpSummary.level?.next_level && (
                  <div className="mt-2">
                    <div className="h-1.5 rounded-full bg-[hsl(var(--secondary))] overflow-hidden">
                      <div className="h-full rounded-full bg-[hsl(var(--primary))] transition-all" style={{
                        width: `${Math.min(100, ((xpSummary.total_xp - xpSummary.level.min_xp) / (xpSummary.level.next_level.min_xp - xpSummary.level.min_xp)) * 100)}%`
                      }} />
                    </div>
                    <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">
                      {xpSummary.level.next_level.min_xp - xpSummary.total_xp} XP to Level {xpSummary.level.next_level.level} {xpSummary.level.next_level.title}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </button>

          {xpSummary.badges?.length > 0 && (
            <div className="border-t border-[hsl(var(--border))] px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">Badges</p>
              <div className="flex flex-wrap gap-2">
                {xpSummary.badges.map((badge, idx) => (
                  <span key={badge.badge_id || `badge-${idx}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-[#C36A2F]/10 text-[#C36A2F] border border-[#C36A2F]/20" data-testid={`my-badge-${badge.badge_id}`}>
                    <Star size={12} /> {badge.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Drop-down content */}
          {progressOpen && (
            <div className="border-t border-[hsl(var(--border))] p-4 space-y-6 bg-[hsl(var(--background))]/30" data-testid="barback-panel">
              {/* Score grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="user-stats-grid">
                <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                  <CardContent className="p-4 text-center">
                    <p className={`text-3xl font-bold ${getScoreColor(avgScore)}`}>{avgScore.toFixed(0)}%</p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Average Score</p>
                  </CardContent>
                </Card>
                <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-[hsl(var(--primary))]">{results.length}</p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Tests Taken</p>
                  </CardContent>
                </Card>
                <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))] col-span-2 md:col-span-1">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-green-500">{bestScore.toFixed(0)}%</p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Best Score</p>
                  </CardContent>
                </Card>
              </div>

              {/* Study Decks */}
              {studyDecks.length > 0 && (
                <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <BookOpen size={16} className="text-[hsl(var(--primary))]" />
                      My Study Decks
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2" data-testid="study-decks">
                    {studyDecks.map((deck) => (
                      <Link key={deck.test_id} to={`/study/${deck.test_id}`}>
                        <div className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--accent))] transition-colors cursor-pointer">
                          <div className="flex items-center gap-2">
                            <BookOpen size={16} className="text-[hsl(var(--primary))]" />
                            <span className="text-sm font-medium">{deck.test_name}</span>
                          </div>
                          <span className="text-xs text-[hsl(var(--muted-foreground))]">Study</span>
                        </div>
                      </Link>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Recent Results */}
              {results.length > 0 ? (
                <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <BarChart2 size={16} className="text-[hsl(var(--primary))]" />
                      Recent Results
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2" data-testid="recent-tests">
                    {results.slice(0, 10).map((result, idx) => (
                      <div
                        key={result.id || `result-${idx}`}
                        className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--accent))] cursor-pointer transition-colors"
                        onClick={() => result.id && setReviewResultId(result.id)}
                        data-testid={`result-row-${idx}`}
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium">{result.test_name || "Test"}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <p className="text-xs text-[hsl(var(--muted-foreground))]">{new Date(result.completed_at).toLocaleDateString()}</p>
                            {result.perfect_cocktails > 0 && (
                              <p className="text-xs text-[hsl(var(--primary))]">
                                <Award size={12} className="inline mr-1" />{result.perfect_cocktails} perfect
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`text-xl font-bold ${getScoreColor(result.percentage)}`}>{result.percentage?.toFixed(0)}%</span>
                          <Eye size={16} className="text-[hsl(var(--muted-foreground))]" />
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ) : (
                <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-4">
                  No test results yet. Take a test to start tracking your progress.
                </p>
              )}
            </div>
          )}
        </Card>
      )}

      {/* ───── Accuracy by Question Type ───── */}
      <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="text-lg flex items-center gap-2">
              <Target size={18} className="text-[hsl(var(--primary))]" />
              Accuracy by Question Type
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              {masteryStats && (masteryStats.mastered_count > 0 || masteryStats.weak_remaining > 0) && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/30" data-testid="mastery-counter" title="Practice question mastery — separate from Challenge Card review below">
                  <Zap size={12} className="text-[hsl(var(--primary))]" />
                  <span className="text-xs font-bold text-[hsl(var(--primary))]" data-testid="mastery-counter-count">{masteryStats.mastered_count} questions mastered</span>
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">· {masteryStats.weak_remaining} left</span>
                </div>
              )}
              <Button size="sm" onClick={() => navigate("/practice-weak")} variant="outline" className="text-xs border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300" data-testid="practice-weak-btn">
                <Target size={12} className="mr-1" /> Practice Weak Areas
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4" data-testid="type-breakdown">
          {Object.entries(typeStats).map(([type, stats]) => {
            if (stats.total === 0) return null;
            const pct = (stats.correct / stats.total) * 100;
            const label = type.charAt(0).toUpperCase() + type.slice(1);
            return (
              <div key={type} data-testid={`type-stat-${type}`}>
                <div className="flex justify-between items-center mb-1">
                  <div className="flex items-center gap-2">
                    {pct >= 80 ? <CheckCircle2 size={14} className="text-green-500" /> : <XCircle size={14} className="text-red-500" />}
                    <span className="text-sm font-medium">{label}</span>
                  </div>
                  <span className={`text-sm font-bold ${getScoreColor(pct)}`}>
                    {pct.toFixed(0)}% ({stats.correct}/{stats.total})
                  </span>
                </div>
                <div className="w-full h-2 rounded-full bg-[hsl(var(--secondary))]">
                  <div className={`h-full rounded-full transition-all ${getBarColor(pct)}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
          {Object.values(typeStats).every(s => s.total === 0) && (
            <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-4">
              Complete some tests to see your accuracy breakdown
            </p>
          )}
        </CardContent>
      </Card>

      {/* ───── Challenge Card Review (Phase 2 card_mastery data) ───── */}
      <CardReviewSummary />
      </div>

      {/* ───── My Tests ───── */}
      <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Play size={18} className="text-[hsl(var(--primary))]" />
            My Tests
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3" data-testid="my-tests-list">
          {myTests.length === 0 ? (
            <div className="flex items-center justify-between gap-3 py-2 flex-wrap" data-testid="my-tests-empty">
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                No tests assigned yet. Ask your venue manager to assign one when you're ready.
              </p>
              <Link to="/flashspecs">
                <Button size="sm" variant="outline" className="border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 shrink-0" data-testid="my-tests-empty-flashspecs-link">
                  <Layers size={14} className="mr-1.5" /> Keep Studying
                </Button>
              </Link>
            </div>
          ) : (
            myTests.map((item) => {
              const t = item.test;
              const a = item.assignment;
              const isExpanded = expandedTest === t.id;
              const lb = testLeaderboards[t.id];

              return (
                <div key={a.id} className="rounded-lg border border-[hsl(var(--border))] overflow-hidden" data-testid={`test-card-${t.id}`}>
                  <div className="p-4 bg-[hsl(var(--secondary))]">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="font-semibold">{t.name}</h3>
                        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                          {t.cocktail_count} cocktails &bull; {t.test_type.replace('_', ' ')} &bull; {item.attempts} attempt{item.attempts !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        {item.attempts > 0 && (
                          <div className="text-right">
                            <p className={`text-lg font-bold ${getScoreColor(item.best_percentage)}`}>
                              {item.best_percentage.toFixed(0)}%
                            </p>
                            <p className="text-[10px] text-[hsl(var(--muted-foreground))]">best</p>
                          </div>
                        )}
                        <Link to={`/take-test/${a.access_code}`}>
                          <Button
                            size="sm"
                            className="bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90"
                            data-testid={`take-test-${t.id}`}
                          >
                            <Play size={14} className="mr-1" />
                            {item.attempts > 0 ? "Retake" : "Start"}
                          </Button>
                        </Link>
                      </div>
                    </div>

                    {item.recent_results.length > 0 && (
                      <div className="flex gap-1 mt-3">
                        {[...item.recent_results].reverse().map((r, idx) => (
                          <div key={r.id || `bar-${idx}`} className="flex-1" title={`${r.percentage?.toFixed(0)}%`}>
                            <div className="h-1.5 rounded-full bg-[hsl(var(--background))]">
                              <div
                                className={`h-full rounded-full ${getBarColor(r.percentage || 0)}`}
                                style={{ width: `${r.percentage || 0}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      <Link to={`/study/${t.id}`}>
                        <Button variant="ghost" size="sm" className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]">
                          <BookOpen size={12} className="mr-1" /> Study
                        </Button>
                      </Link>
                      <Link to={`/flashspecs/study/${t.id}`}>
                        <Button variant="ghost" size="sm" className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]">
                          <Layers size={12} className="mr-1" /> FlashSpecs
                        </Button>
                      </Link>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleLeaderboard(t.id)}
                        className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]"
                        data-testid={`toggle-lb-${t.id}`}
                      >
                        <Trophy size={12} className="mr-1" />
                        Leaderboard
                        {isExpanded ? <ChevronUp size={12} className="ml-1" /> : <ChevronDown size={12} className="ml-1" />}
                      </Button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="p-4 border-t border-[hsl(var(--border))] bg-[hsl(var(--card))]" data-testid={`leaderboard-${t.id}`}>
                      {!lb ? (
                        <p className="text-xs text-[hsl(var(--muted-foreground))] text-center">Loading...</p>
                      ) : lb.leaderboard.length === 0 ? (
                        <p className="text-xs text-[hsl(var(--muted-foreground))] text-center">No scores yet. Be the first!</p>
                      ) : (
                        <div className="space-y-1.5">
                          {lb.leaderboard.map((entry) => (
                            <div
                              key={entry.rank}
                              className={`flex items-center justify-between py-1.5 px-2 rounded text-sm ${
                                entry.rank <= 3 ? 'bg-[hsl(var(--primary))]/5' : ''
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <span className={`w-5 text-center font-bold text-xs ${
                                  entry.rank === 1 ? 'text-[#C36A2F]' : entry.rank === 2 ? 'text-gray-400' : entry.rank === 3 ? 'text-orange-500' : 'text-[hsl(var(--muted-foreground))]'
                                }`}>
                                  {entry.rank}
                                </span>
                                <span className="font-medium text-sm">{entry.staff_name}</span>
                              </div>
                              <div className="flex items-center gap-3 text-xs">
                                <span className="text-[hsl(var(--muted-foreground))]">{entry.attempts}x</span>
                                <span className={`font-bold ${getScoreColor(entry.avg_score)}`}>
                                  {entry.avg_score.toFixed(0)}%
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* ───── Tournaments (secondary feature) ───── */}
      <TournamentSection currentUser={currentUser} />

      {/* ───── Account ───── */}
      <AccountSection user={currentUser} />

      {/* Test Review Dialog */}
      <TestReviewDialog
        resultId={reviewResultId}
        open={!!reviewResultId}
        onClose={() => setReviewResultId(null)}
      />

      {/* Profile / invite-code popup (re-uses sidebar component so the same flow is reachable on mobile) */}
      <UserProfilePopup
        user={currentUser}
        open={profileOpen}
        onOpenChange={setProfileOpen}
        onJoined={() => window.location.reload()}
      />
    </div>
  );
}
